import { defineStore, storeToRefs } from 'pinia'
import { ref, watch, type WatchStopHandle } from 'vue'
import { BaseDevice, ConnectionType, UserDataSyncEvents } from '@rapoo/device-sdk'
import { useConfigName } from '@/composables/useConfigName'
import { DeviceItem, useDeviceStore } from './device'

export type TrayDeviceStateSnapshot = {
  id: string
  name: string
  type: string
  connectionType: ConnectionType
  currentConfigIndex: number
  currentConfigName: string
  battery: number
  batteryStatus: number
  icon: string
}

export const useTrayStore = defineStore('tray', () => {
  const deviceStore = useDeviceStore()
  const { devices } = storeToRefs(deviceStore)

  const deviceState = ref<TrayDeviceStateSnapshot[]>([])

  const deviceReportListeners = new Map<string, { device: BaseDevice; listener: () => void }>()
  let isDesktopStateSyncEnabled = false
  let stopDeviceListWatch: WatchStopHandle | null = null
  let unsubscribeDeviceState: (() => void) | null = null

  const buildDeviceStateSnapshot = (deviceItems: DeviceItem[]): TrayDeviceStateSnapshot[] => {
    return deviceItems.map(({ original }) => ({
      id: original.id,
      name: original.manifest.model,
      type: original.manifest.type,
      connectionType: original.getConnectionType(),
      currentConfigIndex: original.currentConfig?.index,
      currentConfigName: useConfigName(original.currentConfig, original),
      battery: original.userCustomData?.batPercent || 0,
      batteryStatus: original.userCustomData?.batStatus || 0,
      icon: original.manifest.ui.icon,
    }))
  }

  const syncDeviceStateToDesktop = async () => {
    if (!isDesktopStateSyncEnabled || !window.electronAPI?.syncDeviceState) return

    await window.electronAPI.syncDeviceState(buildDeviceStateSnapshot(devices.value))
  }

  const attachDeviceReportListener = (deviceItem: DeviceItem) => {
    const device = deviceItem.original
    if (deviceReportListeners.has(device.id)) return

    const listener = () => {
      void syncDeviceStateToDesktop()
    }

    for (const event of UserDataSyncEvents) {
      device.on(event, listener)
    }
    deviceReportListeners.set(device.id, { device, listener })
  }

  const detachDeviceReportListener = (deviceId: string) => {
    const entry = deviceReportListeners.get(deviceId)
    if (!entry) return

    for (const event of UserDataSyncEvents) {
      entry.device.off(event, entry.listener)
    }
    deviceReportListeners.delete(deviceId)
  }

  const refreshDeviceReportListeners = (devices: DeviceItem[]) => {
    const activeIds = new Set(devices.map((device) => device.id))

    devices.forEach(attachDeviceReportListener)

    for (const deviceId of deviceReportListeners.keys()) {
      if (!activeIds.has(deviceId)) {
        detachDeviceReportListener(deviceId)
      }
    }
  }

  const enableDesktopStateSync = () => {
    if (isDesktopStateSyncEnabled) return

    isDesktopStateSyncEnabled = true
    stopDeviceListWatch = watch(
      devices,
      (val) => {
        refreshDeviceReportListeners(val)
        void syncDeviceStateToDesktop()
      },
      { immediate: true },
    )
  }

  const initTrayState = async () => {
    deviceState.value = (await window.electronAPI?.getDeviceInfo()) ?? []

    if (!unsubscribeDeviceState && window.electronAPI?.onDeviceStateChanged) {
      unsubscribeDeviceState = window.electronAPI.onDeviceStateChanged((state) => {
        deviceState.value = state
      })
    }
  }

  const disposeTrayState = () => {
    unsubscribeDeviceState?.()
    unsubscribeDeviceState = null
  }

  const disposeDesktopStateSync = () => {
    stopDeviceListWatch?.()
    stopDeviceListWatch = null

    for (const deviceId of [...deviceReportListeners.keys()]) {
      detachDeviceReportListener(deviceId)
    }

    isDesktopStateSyncEnabled = false
  }

  return {
    deviceState,
    enableDesktopStateSync,
    initTrayState,
    disposeTrayState,
    disposeDesktopStateSync,
  }
})
